import { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import MainNavbar from "./components/navbar";
import ScrollToTop from "./components/ScrollToTop";
import SiteModeProvider from "./components/SiteModeProvider";
import CookieConsentProvider from "./components/CookieConsent/CookieConsentContext";
import {
  RETAIL_ROUTES,
  CORPORATE_ROUTES,
  CORPORATE_HOME,
  LEGACY_REDIRECTS,
} from "./lib/siteMode";
import { loadArticlePage } from "./content/articles";
import Footer from "./components/footer";
import Container from "react-bootstrap/Container";
// Shipped in the main bundle, not code-split — these are the pages cold
// traffic (a fresh page load, e.g. an ad click) actually lands on, so
// lazy-loading them meant an extra async chunk fetch raced the initial
// render: on slow mobile connections the navbar + empty <main> + footer
// painted first, then the page content popped in seconds later.
import VallalatiHome from "./landings/VallalatiHome";
import LakossagiHome from "./landings/LakossagiHome";
import SzemelyiKolcson from "./landings/retail/SzemelyiKolcson";

// Every other landing page is only ever needed once a visitor actually
// navigates to it, so they're code-split into separate chunks instead of
// all shipping in the main bundle on every page load.
const DualEducation = lazy(() => import("./landings/corporate/DualEducation"));
const NemzetiBajnokok = lazy(() =>
  import("./landings/corporate/NemzetiBajnokok")
);
const NemzetiBajnokokReszletek = lazy(() =>
  import("./landings/corporate/NemzetiBajnokokReszletek")
);
const DimopPlusz512bReszletek = lazy(() =>
  import("./landings/corporate/DimopPlusz512bReszletek")
);
const ElmaradottTersegBruhazas = lazy(() =>
  import("./landings/corporate/ElmaradottTersegBruhazas")
);
const BusinessLoanConsulting = lazy(() =>
  import("./landings/corporate/BusinessLoanConsulting")
);
const CorporateAccountOptimize = lazy(() =>
  import("./landings/corporate/CorporateAccountOptimize")
);
const PrivacyPolicy = lazy(() => import("./landings/PrivacyPolicy"));
const Impresszum = lazy(() => import("./landings/Impresszum"));
const Rolunk = lazy(() => import("./landings/Rolunk"));
const ThanksPage = lazy(() => import("./landings/ThanksPage"));
const ThanksPageRetail = lazy(() =>
  import("./landings/retail/ThanksPageRetail")
);
const KkvAdmin = lazy(() => import("./landings/corporate/KkvAdmin"));
const KkvFinanszirozasiTerkep = lazy(() =>
  import("./landings/corporate/KkvFinanszirozasiTerkep")
);
const Jelzaloghitel = lazy(() => import("./landings/retail/Jelzaloghitel"));
const LakossagiBankszamla = lazy(() =>
  import("./landings/retail/LakossagiBankszamla")
);
const Nyugdijmegtakaritas = lazy(() =>
  import("./landings/retail/Nyugdijmegtakaritas")
);
const Eletbiztositas = lazy(() => import("./landings/retail/Eletbiztositas"));
const ArticlePage = lazy(loadArticlePage);

// Articles used to live at /lakossagi/<slug>; they're at /<slug> now.
function LegacyArticleRedirect() {
  const { articleSlug } = useParams();
  return <Navigate to={`/${articleSlug}`} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <SiteModeProvider>
        <CookieConsentProvider>
          <MainNavbar />
          <main>
            <Suspense
              fallback={
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: "60vh" }}
                >
                  <div
                    className="spinner-border"
                    role="status"
                    style={{ color: "var(--warning-color)" }}
                  >
                    <span className="visually-hidden">Betöltés...</span>
                  </div>
                </div>
              }
            >
              <Routes>
                {/* Lakossági (retail) — the default, at the root */}
                <Route path="/" element={<LakossagiHome />} />
                <Route
                  path={RETAIL_ROUTES.jelzaloghitel}
                  element={<Jelzaloghitel />}
                />
                <Route
                  path={RETAIL_ROUTES.szemelyiKolcson}
                  element={<SzemelyiKolcson />}
                />
                <Route
                  path={RETAIL_ROUTES.bankszamla}
                  element={<LakossagiBankszamla />}
                />
                <Route
                  path={RETAIL_ROUTES.nyugdijmegtakaritas}
                  element={<Nyugdijmegtakaritas />}
                />
                <Route
                  path={RETAIL_ROUTES.eletbiztositas}
                  element={<Eletbiztositas />}
                />

                {/* Vállalati (corporate) — everything under /vallalati */}
                <Route path={CORPORATE_HOME} element={<VallalatiHome />} />
                <Route
                  path={CORPORATE_ROUTES.nemzetiBajnokok}
                  element={<NemzetiBajnokok />}
                />
                <Route
                  path={CORPORATE_ROUTES.nemzetiBajnokokReszletek}
                  element={<NemzetiBajnokokReszletek />}
                />
                <Route
                  path={CORPORATE_ROUTES.dimopPlusz512bReszletek}
                  element={<DimopPlusz512bReszletek />}
                />
                <Route
                  path={CORPORATE_ROUTES.dualisKepzes}
                  element={<DualEducation />}
                />
                <Route
                  path={CORPORATE_ROUTES.elmaradottTersegek}
                  element={<ElmaradottTersegBruhazas />}
                />
                <Route
                  path={CORPORATE_ROUTES.vallalatiHitel}
                  element={<BusinessLoanConsulting />}
                />
                <Route
                  path={CORPORATE_ROUTES.szamlaoptimalizalas}
                  element={<CorporateAccountOptimize />}
                />
                <Route
                  path={CORPORATE_ROUTES.finanszirozasiTerkep}
                  element={<KkvFinanszirozasiTerkep />}
                />
                <Route
                  path={CORPORATE_ROUTES.kkvAdmin}
                  element={<KkvAdmin />}
                />

                {/* Old URLs → current equivalents */}
                {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
                  <Route
                    key={from}
                    path={from}
                    element={<Navigate to={to} replace />}
                  />
                ))}
                <Route
                  path="/lakossagi/:articleSlug"
                  element={<LegacyArticleRedirect />}
                />

                {/* Shared */}
                <Route
                  path="/adatkezelesi-tajekoztato"
                  element={<PrivacyPolicy />}
                />
                <Route path="/impresszum" element={<Impresszum />} />
                <Route path="/rolunk" element={<Rolunk />} />
                <Route path="/koszonjuk" element={<ThanksPage />} />
                <Route
                  path="/koszonjuk-lakossagi"
                  element={<ThanksPageRetail />}
                />

                {/* SEO content articles, at /<slug>. React Router ranks
                    static segments above dynamic ones regardless of
                    declaration order, so this catch-all never shadows any
                    route above; an unknown slug redirects to "/" from
                    inside ArticlePage. */}
                <Route path="/:articleSlug" element={<ArticlePage />} />
              </Routes>
            </Suspense>
          </main>
          <Container className="py-0">
            <Footer />
          </Container>
        </CookieConsentProvider>
      </SiteModeProvider>
    </BrowserRouter>
  );
}
